'use client';

import { DatePicker } from '@koda/koda-components/molecules/DatePicker';
import NumberField from '@koda/koda-components/molecules/NumberField';
import { RadioGroup } from '@koda/koda-components/molecules/RadioGroup';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import { Past5YearsFuture2Years } from './consts/dateIntervals';
import {
  createRestaurantAndCafeDefaults,
  RESTAURANT_AND_CAFE_CONTENT_PATH,
  restaurantAndCafeSchema,
} from './restaurantAndCafe.Schema';

export default function RestaurantAndCafeComponent(): ReactElement {
  const licenseCode = LicenseTypes.RestaurantAndCafe;

  const { values, setField, getError, costCalculated, showErrors } = useLicenseBusiness(licenseCode, {
    schema: restaurantAndCafeSchema,
    defaults: createRestaurantAndCafeDefaults,
  });

  const t = useTranslations(RESTAURANT_AND_CAFE_CONTENT_PATH);

  return (
    <>
      <NumberField
        label={t('venueSize.label')}
        name="area"
        placeholder={t('venueSize.placeholder')}
        required
        formatThousands
        disabled={costCalculated}
        value={values.area}
        onChange={e => setField('area', parseInt(e.target.value))}
        helpText={t('venueSize.helpText')}
        error={values.area || showErrors ? getError('area') : undefined}
        validated={!!values.area && !getError('area')}
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
      <DatePicker
        label={t('startDate.label')}
        name="startDate"
        placeholder={t('startDate.placeholder')}
        disabled={costCalculated}
        value={values.startDate && values.startDate !== '' ? new Date(values.startDate) : undefined}
        onChange={date => setField('startDate', date ? dayjs(date).format('YYYY-MM-DD') : '')}
        error={values.startDate || showErrors ? getError('startDate') : undefined}
        validated={!!values.startDate && !getError('startDate')}
        initialVisibleValue={new Date()}
        interval={Past5YearsFuture2Years}
      />
      <RadioGroup
        title={t('liveMusic.label')}
        description={t('liveMusic.description')}
        options={[
          { value: 'true', label: t('liveMusic.options.yes') },
          { value: 'false', label: t('liveMusic.options.no') },
        ]}
        disabled={costCalculated}
        horizontal={false}
        value={values.hasLiveMusic ? 'true' : 'false'}
        onValueChange={val => setField('hasLiveMusic', val === 'true')}
      />
    </>
  );
}
